import {
  getUniqueDefaultAvatarDataUrl,
  isPlaceholderProfilePictureUrl,
} from '../../utils/uniqueDefaultAvatar';

export { isPlaceholderProfilePictureUrl } from '../../utils/uniqueDefaultAvatar';

/** Dispatched when profile data or avatar changes (header listeners refetch). */
export const PROFILE_UPDATED_EVENT = 'bahlus-profile-updated';

/** Legacy grey asset (settings UI only — prefer buildProfilePictureUrl with identity). */
export function getProfilePictureFallback() {
  return `${process.env.PUBLIC_URL}/profile-pic.svg`;
}

export function getSocialProfilePictureFallback() {
  return `${process.env.PUBLIC_URL}/social-profile-default.svg`;
}

export function getSocialCoverPictureFallback() {
  return `${process.env.PUBLIC_URL}/social-cover-default.svg`;
}

export function uniqueProfileAvatarUrl(identity = {}) {
  return getUniqueDefaultAvatarDataUrl(identity);
}

/** Neutral placeholder before profile loads; pass identity when user is known. */
export function getDefaultAvatarDataUrl(identity = {}) {
  return uniqueProfileAvatarUrl(identity);
}

function resolveStoredAssetUrl(trimmed, baseUrl) {
  const u = String(trimmed || '').trim();
  if (!u) return '';
  if (u.startsWith('data:')) return u;
  if (u.startsWith('//')) return `https:${u}`;
  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u);
      if (parsed.pathname.startsWith('/uploads/')) {
        return `${parsed.pathname}${parsed.search}`;
      }
    } catch {
      /* ignore */
    }
    return u;
  }
  const base = String(baseUrl || '').replace(/\/$/, '');
  return `${base}/${u.replace(/^\/+/, '')}`;
}

function resolveAvatarTheme(identity = {}) {
  if (identity?.theme === 'dark' || identity?.theme === 'light') {
    return identity.theme;
  }
  if (typeof document !== 'undefined') {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  }
  return 'light';
}

function uniqueAvatarForIdentity(identity = {}) {
  return getUniqueDefaultAvatarDataUrl({
    userId: identity?.userId,
    name: identity?.name || identity?.displayName,
    theme: resolveAvatarTheme(identity),
  });
}

export function applyProfileAvatarImgFallback(imgEl, identity = {}) {
  if (!imgEl || imgEl.dataset.avatarFallbackApplied === '1') return;
  imgEl.dataset.avatarFallbackApplied = '1';
  const fallback = uniqueAvatarForIdentity(identity);
  if (imgEl.src !== fallback) {
    imgEl.src = fallback;
  }
}

export function buildProfilePictureUrl(
  rawUrl,
  baseUrl = process.env.REACT_APP_BASE_URL || '',
  identity = {}
) {
  const trimmed = String(rawUrl || '').trim();
  if (trimmed.startsWith('data:')) return trimmed;
  if (isPlaceholderProfilePictureUrl(trimmed)) {
    return uniqueAvatarForIdentity(identity);
  }
  const resolved = resolveStoredAssetUrl(trimmed, baseUrl);
  if (isPlaceholderProfilePictureUrl(resolved)) {
    return uniqueAvatarForIdentity(identity);
  }
  return resolved;
}

export function buildSocialProfilePictureUrl(
  rawUrl,
  baseUrl = process.env.REACT_APP_BASE_URL || '',
  identity = {}
) {
  const trimmed = String(rawUrl || '').trim();
  if (trimmed.startsWith('data:')) return trimmed;
  if (isPlaceholderProfilePictureUrl(trimmed)) {
    return uniqueAvatarForIdentity(identity);
  }
  const resolved = resolveStoredAssetUrl(trimmed, baseUrl);
  if (isPlaceholderProfilePictureUrl(resolved)) {
    return uniqueAvatarForIdentity(identity);
  }
  return resolved;
}

export function buildCoverPictureUrl(rawUrl, baseUrl = process.env.REACT_APP_BASE_URL || '') {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  const trimmed = rawUrl.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:')) return trimmed;
  const base = String(baseUrl || '').replace(/\/$/, '');
  return `${base}/${trimmed.replace(/^\/+/, '')}`;
}

export function buildSocialCoverPictureUrl(rawUrl, baseUrl = process.env.REACT_APP_BASE_URL || '') {
  const fallback = getSocialCoverPictureFallback();
  if (!rawUrl || typeof rawUrl !== 'string') return fallback;
  const trimmed = rawUrl.trim();
  if (!trimmed) return fallback;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:')) return trimmed;
  const base = String(baseUrl || '').replace(/\/$/, '');
  return `${base}/${trimmed.replace(/^\/+/, '')}`;
}

export function buildProfilePictureUrlForMode(
  rawUrl,
  baseUrl = process.env.REACT_APP_BASE_URL || '',
  profileMode = 'personal',
  identity = {}
) {
  return String(profileMode || '').trim().toLowerCase() === 'social'
    ? buildSocialProfilePictureUrl(rawUrl, baseUrl, identity)
    : buildProfilePictureUrl(rawUrl, baseUrl, identity);
}

/** @deprecated Use buildProfilePictureUrlForMode (same behavior). */
export function buildProfilePictureDisplayUrlForMode(
  rawUrl,
  baseUrl = process.env.REACT_APP_BASE_URL || '',
  profileMode = 'personal',
  identity = {}
) {
  return buildProfilePictureUrlForMode(rawUrl, baseUrl, profileMode, identity);
}

export function dispatchProfileUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT));
  }
}
